import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Spin, Button, Modal, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, AuditOutlined } from "@ant-design/icons"; // Icons from Ant Design
import StockTable from "../StockTable/StockTable";

// import ShippingCreationForm from "../../Forms/ShippingCreationForm/ShippingCreationForm"
const ShippingDashboardTable = ({
  history,
  loading,
  data,
  callReFetch,
  dispatch,
}) => {
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Update isMobileView based on window shipping
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleOpenEditModal = async (id) => {
    const shippingToEdit = data.find((shipping) => shipping._id === id);
    if (shippingToEdit) {
      setSelectedShipping(shippingToEdit);
      setIsEditModalVisible(true);
    }
  };

  const handleTableOnChange = (pagination, filteredInfo, sorter) => {
    dispatch({ type: "sorter", payload: sorter });
    dispatch({ type: "filteredInfo", payload: filteredInfo });
    // Update pagination state
    if (pagination) {
      dispatch({ type: "current", payload: pagination.current });
      dispatch({ type: "pageSize", payload: pagination.pageSize });
    }
    callReFetch();
  };

  const statusFilter = [
    { text: "In Stock", value: "in stock" },
    { text: "In Delivery", value: "in delivery" },
  ];

  const quantityDiffFilterOptions = [
    { text: "Has Difference (Excl. In Delivery)", value: "has_diff" },
  ];
  const renderFilter = (items) => {
    const filterField = {
      status: statusFilter,
      days_until_eta: [
        { text: "Blank", value: "blank" },
        { text: "Negative", value: "negative" },
      ],
      counterCode: data.data
        ? [...new Set(data.data.map((item) => item.counterCode))]
            .filter(Boolean)
            .map((code) => ({ text: code, value: code }))
        : [],
      noForm: null,
      noSJ: null,
    };

    return filterField[items];
  };

  const columns = [
    {
      title: "Tanggal Kirim",
      dataIndex: "tanggal_kirim",
      key: "tanggal_kirim",
      sorter: true,
      render: (date) => {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
      },
    },
    {
      title: "Tanggal Terima ETA",
      dataIndex: "tanggal_terima_eta",
      key: "tanggal_terima_eta",
      sorter: true,
      render: (date) => {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
      },
    },
    {
      title: "Late by (days)",
      dataIndex: "days_until_eta",
      key: "days_until_eta",
      sorter: true,
      filters: renderFilter("days_until_eta"),
      onFilter: (value, record) => {
        return value === "blank"
          ? !record.days_until_eta || record.days_until_eta >= 0
          : record.days_until_eta < 0;
      },
    },

    {
      title: "No Bon",
      dataIndex: "noForm",
      key: "noForm",
      sorter: true,
      filterSearch: true,
      onFilter: (value, record) => record.noForm.includes(value),
    },
    {
      title: "No Surat Jalan",
      dataIndex: "noSJ",
      key: "noSJ",
      sorter: true,
      filterSearch: true,
      onFilter: (value, record) => record.noSJ.includes(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: renderFilter("status"),
    },
    {
      title: "Kirim Ke",
      dataIndex: "counterCode",
      key: "counterCode",
      sorter: true,
      filters: renderFilter("counterCode"),
    },
    {
      title: "Article",
      dataIndex: "article",
      key: "article",
      sorter: true,
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      sorter: true,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      sorter: true,
    },
    {
      title: "Qty Kirim",
      dataIndex: "quantity",
      key: "quantity",
      sorter: true,
    },

    {
      title: "Qty Terima",
      dataIndex: "receivedQuantity",
      key: "receivedQuantity",
      sorter: true,
    },
    {
      title: "Qty Difference",
      dataIndex: "quantity_diff",
      sorter: (a, b) => {
        // Handle sorting for "in delivery" status
        const aValue = a.status === "in delivery" ? null : a.quantity_diff;
        const bValue = b.status === "in delivery" ? null : b.quantity_diff;
        
        // Sort null values to the end
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
        
        // Sort numeric values
        return aValue - bValue;
      },
      filters: quantityDiffFilterOptions,
      onFilter: (value, record) => {
        return value === "has_diff"
          ? record.quantity_diff != 0
          : record.quantity_diff = 0;
      },
      render: (value, record) => {
        // Hide quantity difference when status is "in delivery"
        if (record.status === "in delivery") {
          return null;
        }
        return value;
      },
    },
  ];

  // Ensure data is an array before passing to Table
  // const tableData = Array.isArray(data) ? data : [];
  return (
    <>
      <div style={{ width: "80vw" }}>
        <Spin tip="Loading shipping data..." spinning={loading}>
          <Table
            columns={columns}
            dataSource={data.data}
            pagination={
              data.pagination
                ? {
                    current: data.pagination.current,
                    pageSize: data.pagination.pageSize,
                    total: data.pagination.total,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }
                : false
            }
            onChange={(pagination, filteredInfo, sorter) =>
              handleTableOnChange(pagination, filteredInfo, sorter)
            }
          />
        </Spin>
      </div>
    </>
  );
};

ShippingDashboardTable.propTypes = {};

export default ShippingDashboardTable;
